import React, { useEffect, useState, useCallback, useRef } from 'react';
import { SimpleBarChart } from '@carbon/charts-react';
import { SkeletonPlaceholder, TooltipIcon } from 'carbon-components-react';
import Utils from 'utilities/helper.util';
import { useEventListener } from 'utilities/hooks.util';
import { convertData, getTicksFormatter, customToolTipHtml } from './helper';
import { InformationFilled16 } from '@carbon/icons-react';

const Chart: React.FC<Props> = ({
  title,
  isLoading,
  startDate,
  range,
  chartData,
  tooltipText
}) => {
  const [data, setData] = useState<any[]>();
  const [options, setOptions] = useState({});
  const [isShow, setIsShow] = useState<boolean>(false);
  const [ticksValues, setTicksValues] = useState<string[]>();
  const containerRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<string>(range);
  const startDateRef = useRef<string>(startDate);
  const [isHover, setIsHover] = useState(false);

  useEffect(() => {
    rangeRef.current = range;
  }, [range]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    if (chartData?.daily && Array.isArray(chartData?.daily)) {
      setData(
        convertData(
          chartData.daily,
          true,
          startDateRef.current,
          rangeRef.current
        )
      );
    } else if (chartData?.hourly && Array.isArray(chartData?.hourly)) {
      setData(
        convertData(
          chartData.hourly,
          false,
          startDateRef.current,
          rangeRef.current
        )
      );
    }
  }, [chartData]);

  useEffect(() => {
    if (data) {
      let filtered = [];
      switch (rangeRef.current) {
        case 'today': {
          filtered = data.filter((element: any, index: number) => {
            return index === 0 || index % 6 === 0 || index === 23;
          });
          break;
        }
        case 'this-week':
        case 'last-7-days': {
          filtered = data;
          break;
        }
        case 'last-30-days': {
          filtered = data.filter((element: any, index: number) => {
            return index === 0 || (index + 1) % 5 === 0;
          });
          break;
        }
        default: {
          break;
        }
      }
      setTicksValues(
        filtered.map((element: any) => {
          return element.date;
        })
      );
    }
  }, [data]);

  useEffect(() => {
    if (ticksValues) {
      const options: any = {
        axes: {
          right: {
            mapsTo: 'value'
          },
          bottom: {
            mapsTo: 'date',
            scaleType: 'time',
            includeZero: true,
            ticks: {
              rotation: 'never',
              formatter: (t: any) => getTicksFormatter(t, rangeRef.current),
              values: ticksValues
            }
          }
        },
        bars: {
          width: 6
        },
        grid: {
          x: {
            enabled: false
          },
          y: {
            enabled: false
          }
        },
        legend: {
          enabled: false
        },
        color: {
          scale: {
            enquiry: '#a6c8ff'
          }
        },
        tooltip: {
          customHTML: (items: any) => customToolTipHtml(items, rangeRef.current)
        }
      };
      setOptions(options);
    }
  }, [ticksValues]);

  useEffect(() => {
    if (isLoading) {
      setData(undefined);
      setOptions({});
      setTicksValues(undefined);
    }
  }, [isLoading]);

  useEffect(() => {
    if (containerRef?.current) {
      const style = window.getComputedStyle(containerRef.current);
      setIsShow(style.display !== 'none');
    }
  }, [containerRef]);

  const handleWindowResize = useCallback(() => {
    if (containerRef?.current) {
      const style = window.getComputedStyle(containerRef.current);
      setIsShow(style.display !== 'none');
    }
  }, [containerRef]);

  useEventListener('resize', handleWindowResize, window);

  return (
    <div
      ref={containerRef}
      className="dashboard-component--chart"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {isShow && (
        <>
          <div className="component-title">
            {title || 'BYDA enquiries'}
            {isHover && tooltipText ? (
              <TooltipIcon
                tooltipText={tooltipText || ''}
                className="dashboard-tooltip-info"
              >
                <InformationFilled16 className="dashboard-tooltip-info-icon" />
              </TooltipIcon>
            ) : null}
          </div>
          <div className="chart-container">
            {isLoading ? (
              <SkeletonPlaceholder />
            ) : (
              <>
                {data && !Utils.isObjectEmpty(options) && (
                  <SimpleBarChart data={data} options={options} />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

type Props = {
  title?: string;
  isLoading: boolean;
  startDate: string;
  range: string;
  chartData?: {
    daily?: any[];
    hourly?: any[];
    total: number;
  };
  tooltipText?: string;
};

export default React.memo(Chart);
